import * as THREE from 'three';
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import createDepthPass from './depthPass.js';
import { createRenderLoop } from './renderLoop.js';
import { createCamera } from './camera.js';
import { showLabelEl, hideLabelEl, liftLabelEl, unliftLabelEl, setPersistent, disposeLabel } from '../ui/labels.js';
import logger from '../util/logger.js';
import { createSensorManager } from './sensorManager.js';

export function createViewer({ canvas }) {
	let _disposed = false;
	const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
	renderer.setPixelRatio(window.devicePixelRatio || 1);
	renderer.setSize(canvas.clientWidth, canvas.clientHeight, false);
	renderer.setClearColor(0x888888, 1);

	const scene = new THREE.Scene();
	scene.background = new THREE.Color(0x888888);

	const camObj = createCamera(canvas);
	const { camera, controls, fitToBox, setBoundsForBox } = camObj;

	const ambient = new THREE.AmbientLight(0xffffff, 0.3);
	scene.add(ambient);
	const dirLight = new THREE.DirectionalLight(0xffffff, 0.6);
	dirLight.position.set(0, 0, 1000);
	scene.add(dirLight);
	const hemi = new THREE.HemisphereLight(0xffffff, 0x888888, 0.8);
	scene.add(hemi);

	const model = new THREE.Object3D();
	scene.add(model);
	const sensors = new THREE.Object3D();
	scene.add(sensors);

	const labelRenderer = new CSS2DRenderer();
	labelRenderer.sortObjects = false;
	labelRenderer.setSize(canvas.clientWidth, canvas.clientHeight);
	labelRenderer.domElement.style.position = 'absolute';
	labelRenderer.domElement.style.top = '0px';
	labelRenderer.domElement.style.left = '0px';
	labelRenderer.domElement.style.zIndex = '10';
	labelRenderer.domElement.style.pointerEvents = 'none';
	if (canvas && canvas.parentElement) canvas.parentElement.appendChild(labelRenderer.domElement);

	// sensor manager handles id map and disposal
	const sensorManager = createSensorManager({ sensors, disposeLabel, externalLogger: logger });
	const sensorsById = sensorManager._sensorsById;
	// Queue for persistence requests received before sensor mesh is added
	const pendingPersistent = new Map();

	const depthPass = createDepthPass ? createDepthPass(renderer) : null;
	const renderLoop = createRenderLoop({ renderer, scene, camera, controls, labelRenderer, depthPass, sensors, logger });
	renderLoop.start();

	const raycaster = new THREE.Raycaster();
	const pointer = new THREE.Vector2();
	let hoveredObj = null;
	let hoverCb = null;
	const modelClickCbs = [];
	const sensorClickCbs = [];
	// legacy: `onHover` uses hoverCb for enter/move/leave events on sensors
	let selectedSet = new Set();
	let leaveTimeout = null;

	// Perform a single raycast and return typed hits: { type: 'sensor'|'model', object, point, distance }
	function performPick() {
		const pickTargets = sensors.children.slice().concat(model.children.slice());
		const intersects = raycaster.intersectObjects(pickTargets, true) || [];
		const hits = [];
		for (let i = 0; i < intersects.length; i++) {
			const hit = intersects[i];
			let obj = hit.object;
			// climb to find sensor owner if present
			let isSensor = false;
			let owner = obj;
			while (owner && owner !== sensors && owner !== model && owner !== scene) {
				// ignore preview helper meshes created by sensorEditor
				if (owner.userData && owner.userData.__preview) { owner = null; break; }
				if (owner.userData && owner.userData.sensor) { isSensor = true; break; }
				owner = owner.parent;
			}
			// if owner was nulled due to preview, skip this intersect
			if (!owner && obj && obj.userData && obj.userData.__preview) continue;
			if (isSensor) {
				hits.push({ type: 'sensor', object: owner || obj, point: hit.point.clone(), distance: hit.distance });
			} else {
				// skip model hits on preview helpers
				if (obj && obj.userData && obj.userData.__preview) continue;
				hits.push({ type: 'model', object: obj, point: hit.point.clone(), distance: hit.distance });
			}
		}
		return hits;
	}


	function _onPointerUp(ev) {
		if (_disposed) return;
		try {
			// ignore clicks while user is dragging via controls
			if (controls && controls.isDragging) return;
			const rect = canvas.getBoundingClientRect();
			pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
			pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
			raycaster.setFromCamera(pointer, camera);
			const hits = performPick();
			if (hits && hits.length) {
				for (let i = 0; i < hits.length; i++) {
					const hit = hits[i];
					if (hit.type === 'sensor') {
						const obj = hit.object;
						const s = obj && obj.userData ? obj.userData.sensor : null;
						// Also notify hover listeners that this object was selected (click selection).
						if (typeof hoverCb === 'function') {
							const sensorSelected = hoverCb({ object: obj, sensor: s, domElement: obj.userData._labelEl, point: hit.point, selected: selectedSet.has(obj) });
							if (sensorSelected === true) return; // if handler returns true, skip default toggle behavior
						}
						// notify sensor click listeners
						sensorClickCbs.forEach(cb => { try { cb({ object: obj, sensor: s, point: hit.point, distance: hit.distance }); } catch (e) { console.warn(e); } });
						// Toggle persistent state and dispatch a DOM event so
						// host/listening wrappers (React) receive the same
						// `label:persistent` event that DOM label clicks emit.
						let newPersistentState = false;
						if (selectedSet.has(obj)) {
							// deselect this sensor only
							newPersistentState = false;
							selectedSet.delete(obj);
							const elOld = obj.userData._labelEl;
							if (elOld) setPersistent(elOld, false);
						} else {
							// make this sensor persistent (add to set)
							newPersistentState = true;
							selectedSet.add(obj);
							showLabelEl(obj.userData._labelEl, { persistent: true });
							const el = obj.userData._labelEl; if (el) setPersistent(el, true);
						}

						// Dispatch the same CustomEvent as the DOM label handler so
						// persistence is handled uniformly and propagated to wrappers.
						try {
							const parent = canvas && canvas.parentElement ? canvas.parentElement : (typeof document !== 'undefined' ? document : null);
							if (parent) {
								const uid = obj && obj.userData ? (obj.userData._sensorUid || (obj.userData.sensor && (obj.userData.sensor._sensorUid || obj.userData.sensor.id))) : null;
								const evt = new CustomEvent('label:persistent', { detail: { sensorUid: uid, persistent: newPersistentState }, bubbles: true, composed: true });
								parent.dispatchEvent(evt);
							}
						} catch (err) { logger.warn && logger.warn('viewer: dispatch label:persistent failed', err); }
					} else if (hit.type === 'model') {
						// model surface hit: notify registered model callbacks
						modelClickCbs.forEach(cb => { try { cb({ object: hit.object, point: hit.point, distance: hit.distance }); } catch (e) { console.warn(e); } });
					}
				}
			}
		} catch (e) { /* ignore */ }
	}

	canvas.addEventListener('pointermove', _onPointerMove);
	canvas.addEventListener('pointerup', _onPointerUp);
	canvas.addEventListener('pointerout', _onPointerOut);

	function onWindowResize() {
		const w = canvas.clientWidth || window.innerWidth;
		const h = canvas.clientHeight || window.innerHeight;
		camera.aspect = w / h;
		camera.updateProjectionMatrix();
		renderer.setSize(w, h, false);
		labelRenderer.setSize(w, h);
	}
	window.addEventListener('resize', onWindowResize);

	function addSensor(sensorMesh) {
		sensorManager.addSensor(sensorMesh);
	}

	// Listen for label persistence toggles dispatched from labels.js.
	// We attach on the canvas parent so bubbled events from label DOMs are caught.
	function _handleLabelPersistent(ev) {
		try {
			if (_disposed) return;
			const detail = ev && ev.detail ? ev.detail : null;
			if (!detail) return;
			// label:persistent event
			const uid = detail.sensorUid || detail.sensorId || null;
			const persistent = !!detail.persistent;
			if (!uid) return;
			const mesh = sensorsById.get(String(uid));
			if (!mesh) {
				// sensor not yet registered; remember desired persistent state and
				// apply when the sensor is later added via addSensor(). This
				// handles the common case where the React wrapper restores
				// persistence before the GLTF/sensors have been created.
				pendingPersistent.set(String(uid), !!persistent);
				return;
			}

			if (persistent) {
				selectedSet.add(mesh);
				showLabelEl(mesh.userData && mesh.userData._labelEl, { persistent: true });
			} else {
				selectedSet.delete(mesh);
				const el = mesh.userData && mesh.userData._labelEl;
				if (el) setPersistent(el, false);
			}
		} catch (err) { logger.warn && logger.warn('viewer: handleLabelPersistent failed', err); }
	}
	if (canvas && canvas.parentElement) canvas.parentElement.addEventListener('label:persistent', _handleLabelPersistent);

	// Listen for DOM hover events from labels so hoveredObj reflects DOM hover
	if (canvas && canvas.parentElement) {
		function _handleLabelEnter(ev) {
			try {
				if (_disposed) return;
				const detail = ev && ev.detail ? ev.detail : null;
				if (!detail) return;
				// label:enter event
				const uid = detail.sensorUid || detail.sensorId || null;
				if (!uid) return;
				const mesh = sensorsById.get(String(uid));
				if (!mesh) return;
				// skip if mesh disposed
				if (mesh.__disposed) return;
				hoveredObj = mesh;
				// hoveredObj set
				if (!selectedSet.has(mesh)) _toggleLabel(mesh, true);
			} catch (err) { logger.warn && logger.warn('viewer: handleLabelEnter failed', err); }
		}
		function _handleLabelLeave(ev) {
			try {
				if (_disposed) return;
				const detail = ev && ev.detail ? ev.detail : null;
				if (!detail) return;
				// label:leave event
				const uid = detail.sensorUid || detail.sensorId || null;
				if (!uid) return;
				const mesh = sensorsById.get(String(uid));
				if (!mesh) return;
				if (mesh.__disposed) return;
				if (!selectedSet.has(mesh)) {
					_toggleLabel(mesh, false);
				}
				if (hoveredObj === mesh) hoveredObj = null;
			} catch (err) { logger.warn && logger.warn('viewer: handleLabelLeave failed', err); }
		}
		canvas.parentElement.addEventListener('label:enter', _handleLabelEnter);
		canvas.parentElement.addEventListener('label:leave', _handleLabelLeave);
	}

	// hotkeys: 'p' toggle all sensors persistent
	function _onKeyDown(ev) {
		if (!ev || ev.repeat) return;
		const key = ev.key && ev.key.toLowerCase();
		const active = document.activeElement;
		if (active && (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)) return;


		if (key === 'p') {
			// if any sensor is not persistent, make all persistent; otherwise clear all
			let anyNot = false;
			sensors.children.forEach(c => { if (c.isMesh && !selectedSet.has(c)) anyNot = true; });
			if (anyNot) {
				sensors.traverse(o => {
					if (o.isMesh && o.userData && o.userData._labelEl) {
						selectedSet.add(o);
						showLabelEl(o.userData._labelEl, { persistent: true });
					}
				});
			} else {
				sensors.traverse(o => {
					if (o.isMesh && o.userData && o.userData._labelEl) {
						selectedSet.delete(o);
						setPersistent(o.userData._labelEl, false);
						if (hoveredObj !== o) {
							hideLabelEl(o.userData._labelEl, { force: true });
						}
					}
				});
			}
			return;
		}
	}
	window.addEventListener('keydown', _onKeyDown);

	// internal: toggle label visibility via centralized helpers
	function _toggleLabel(obj, show) {
		if (!obj || !obj.userData) return;
		const el = obj.userData._labelEl;
		if (!el) return;
		if (show) {
			showLabelEl(el);
			try { liftLabelEl(el); } catch (err) { logger.warn && logger.warn('viewer: liftLabelEl failed', err); }
		} else {
			try { unliftLabelEl(el); } catch (err) { logger.warn && logger.warn('viewer: unliftLabelEl failed', err); }
			hideLabelEl(el);
		}
	}

	function _onPointerOut() {
		if (leaveTimeout) { clearTimeout(leaveTimeout); leaveTimeout = null; }
		if (hoveredObj) {
			if (!selectedSet.has(hoveredObj)) { _toggleLabel(hoveredObj, false); }
			else { try { unliftLabelEl(hoveredObj.userData && hoveredObj.userData._labelEl); } catch (err) { logger.warn && logger.warn('viewer: unliftLabelEl failed', err); } }
			if (typeof hoverCb === 'function') hoverCb({ object: hoveredObj, sensor: hoveredObj.userData.sensor, domElement: hoveredObj.userData._labelEl, point: null, event: 'leave' });
		}
		hoveredObj = null;
		if (typeof hoverCb === 'function') hoverCb(null);
	}

	function _onPointerMove(ev) {
		const rect = renderer.domElement.getBoundingClientRect();
		pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
		pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
		raycaster.setFromCamera(pointer, camera);
		const pickTargets = sensors.children.slice();
		const intersects = raycaster.intersectObjects(pickTargets, true) || [];
		let topSensor = null;
		for (let i = 0; i < intersects.length; i++) {
			let obj = intersects[i].object;
			// skip preview helper meshes
			if (obj && obj.userData && obj.userData.__preview) continue;
			while (obj && obj !== sensors && obj !== scene) {
				if (obj.userData && obj.userData.sensor) { topSensor = obj; break; }
				obj = obj.parent;
			}
			if (topSensor) { break; }
		}

		if (leaveTimeout) { clearTimeout(leaveTimeout); leaveTimeout = null; }
		if (topSensor !== hoveredObj) {
			if (hoveredObj) {
				if (!selectedSet.has(hoveredObj)) {
					_toggleLabel(hoveredObj, false);
					if (typeof hoverCb === 'function') hoverCb({ object: hoveredObj, sensor: hoveredObj.userData.sensor, domElement: hoveredObj.userData._labelEl, point: null, event: 'leave' });
				} else {
					try { unliftLabelEl(hoveredObj.userData && hoveredObj.userData._labelEl); } catch (err) { logger.warn && logger.warn('viewer: unliftLabelEl failed', err); }
					if (typeof hoverCb === 'function') hoverCb({ object: hoveredObj, sensor: hoveredObj.userData.sensor, domElement: hoveredObj.userData._labelEl, point: null, event: 'leave' });
				}
			}

			hoveredObj = topSensor;
			if (hoveredObj) {
				try { liftLabelEl(hoveredObj.userData && hoveredObj.userData._labelEl); } catch (err) { logger.warn && logger.warn('viewer: liftLabelEl failed', err); }
				if (!selectedSet.has(hoveredObj)) { _toggleLabel(hoveredObj, true); }
				if (typeof hoverCb === 'function') hoverCb({ object: hoveredObj, sensor: hoveredObj.userData.sensor, domElement: hoveredObj.userData._labelEl, point: intersects[0] && intersects[0].point, event: 'enter' });
			}
			if (!hoveredObj && typeof hoverCb === 'function') hoverCb(null);
		} else {
			if (hoveredObj && typeof hoverCb === 'function') { hoverCb({ object: hoveredObj, sensor: hoveredObj.userData.sensor, domElement: hoveredObj.userData._labelEl, point: intersects[0] && intersects[0].point, event: 'move' }); }
		}

		if (!topSensor) {
			if (!leaveTimeout) {
				leaveTimeout = setTimeout(() => {
					leaveTimeout = null;
					if (hoveredObj) {
						if (!selectedSet.has(hoveredObj)) { _toggleLabel(hoveredObj, false); }
						else { try { unliftLabelEl(hoveredObj.userData && hoveredObj.userData._labelEl); } catch (err) { logger.warn && logger.warn('viewer: unliftLabelEl failed', err); } }
						if (typeof hoverCb === 'function') hoverCb({ object: hoveredObj, sensor: hoveredObj.userData.sensor, domElement: hoveredObj.userData._labelEl, point: null, event: 'leave' });
					}
					hoveredObj = null;
					if (typeof hoverCb === 'function') hoverCb(null);
				}, 100);
			}
		}
	}

	function addMesh(mesh) {
		// Ensure geometry has normals for correct lighting. Compute if missing.
		mesh.traverse(c => {
			if (c.isMesh) {
				try {
					const geom = c.geometry;
					if (geom && !geom.attributes?.normal) {
						if (typeof geom.computeVertexNormals === 'function') {
							geom.computeVertexNormals();
						}
					}
					// apply a cheap toon/cell shading material for better face separation (like original viewer)
					try {
						const oldMat = c.material;
						if (!(oldMat instanceof THREE.ShaderMaterial)) {
							const lam = new THREE.MeshToonMaterial({
								color: oldMat && oldMat.color ? oldMat.color.clone() : new THREE.Color(0x444444),
								map: oldMat && oldMat.map ? oldMat.map : null,
								vertexColors: true,
								side: THREE.DoubleSide
							});
							if (oldMat && oldMat.opacity !== undefined) { lam.opacity = oldMat.opacity; lam.transparent = oldMat.transparent; }
							c.material = lam;
							try { c.material.needsUpdate = true; } catch (err) { /* ignore */ }
						}
					} catch (err) { /* ignore */ }
				} catch (e) { /* ignore */ }
				c.castShadow = true;
				c.receiveShadow = true;
			}
		});
		model.add(mesh);
	}

	// Add a sensor mesh to the sensors group and apply any pending persistent
	// state that was requested before the sensor was created.
	function addSensor(sensorMesh) {
		sensorManager.addSensor(sensorMesh);
		try {
			const sid = sensorMesh && sensorMesh.userData && sensorMesh.userData._sensorUid ? String(sensorMesh.userData._sensorUid) : null;
			if (sid && pendingPersistent.has(sid)) {
				const persistentWanted = !!pendingPersistent.get(sid);
				pendingPersistent.delete(sid);
				if (persistentWanted) {
					selectedSet.add(sensorMesh);
					showLabelEl(sensorMesh.userData && sensorMesh.userData._labelEl, { persistent: true });
					const el = sensorMesh.userData && sensorMesh.userData._labelEl; if (el) setPersistent(el, true);
				} else {
					selectedSet.delete(sensorMesh);
					const el = sensorMesh.userData && sensorMesh.userData._labelEl; if (el) setPersistent(el, false);
				}
			}
		} catch (err) { logger.warn && logger.warn('viewer: apply pending persistent failed', err); }
	}

	function clear() { while (model.children.length) { const m = model.children.pop(); try { if (m.geometry) m.geometry.dispose(); } catch (e) { } try { if (m.material) m.material.dispose(); } catch (e) { } } }

	function fitToScene() {
		const box = new THREE.Box3().setFromObject(model);
		if (box.isEmpty()) return;
		if (camObj && typeof camObj.setBoundsForBox === 'function') camObj.setBoundsForBox(box, { offset: 1.1 });
		fitToBox(box);
	}

	function focusSensor(mesh, { offset = 2, animate = true, duration = 450 } = {}) {
		if (!mesh) return;
		try {
			if (controls && controls.isDragging) return;
			const s = mesh.userData && mesh.userData.sensor ? mesh.userData.sensor : {};
			const pos = new THREE.Vector3(s.x || 0, s.y || 0, s.z || 0);
			const box = new THREE.Box3().setFromCenterAndSize(pos, new THREE.Vector3(10, 10, 10));
			if (typeof camObj.fitToBox === 'function') {
				camObj.fitToBox(box, { offset, animate, duration });
			}
		} catch (err) { logger.warn && logger.warn('viewer: focusSensor failed', err); }
	}

	function onHover(cb) { hoverCb = cb; }
	function onModelClick(cb) { if (typeof cb === 'function') modelClickCbs.push(cb); return () => { const i = modelClickCbs.indexOf(cb); if (i >= 0) modelClickCbs.splice(i, 1); }; }
	function onSensorClick(cb) { if (typeof cb === 'function') sensorClickCbs.push(cb); return () => { const i = sensorClickCbs.indexOf(cb); if (i >= 0) sensorClickCbs.splice(i, 1); }; }

	function dispose() {
		// mark disposed early to prevent async callbacks from operating on freed resources
		_disposed = true;
		try { logger.debug && logger.debug('viewer: dispose - stopping renderLoop'); } catch (e) { }
		try { renderLoop.stop(); } catch (e) { }
		try { if (canvas && canvas.removeEventListener) { canvas.removeEventListener('pointermove', _onPointerMove); canvas.removeEventListener('pointerup', _onPointerUp); canvas.removeEventListener('pointerout', _onPointerOut); } } catch (e) { }
		try {
			if (canvas && canvas.parentElement) {
				canvas.parentElement.removeEventListener('label:persistent', _handleLabelPersistent);
				canvas.parentElement.removeEventListener('label:enter', _handleLabelEnter);
				canvas.parentElement.removeEventListener('label:leave', _handleLabelLeave);
			}
		} catch (e) { }
		try { window.removeEventListener('keydown', _onKeyDown); } catch (e) { }
		try { if (camObj && typeof camObj.dispose === 'function') camObj.dispose(); } catch (e) { }
		try { logger.debug && logger.debug('viewer: dispose - removing sensors'); } catch (e) { }
		try { if (sensorManager && typeof sensorManager.removeAllSensors === 'function') sensorManager.removeAllSensors(); } catch (e) { }
		try { if (canvas && canvas.parentElement && labelRenderer && labelRenderer.domElement) labelRenderer.domElement.parentElement.removeChild(labelRenderer.domElement); } catch (e) { }
		try { while (sensors.children.length) sensors.remove(sensors.children[0]); } catch (e) { }
		try { while (model.children.length) model.remove(model.children[0]); } catch (e) { }
		try { renderer.dispose(); } catch (e) { }
		try { hoverCb = null; modelClickCbs.length = 0; selectedSet.clear(); } catch (e) { }
	}

	return {
		addMesh,
		addSensor,
		clear,
		fitToScene,
		focusSensor,
		_getModel: () => model,
		_getSensors: () => sensors,
		_getCamera: () => camera,
		onHover,
		onModelClick,
		onSensorClick,
		dispose,
		_isDisposed: () => _disposed
	};
}

export default createViewer;
